// §34.2 customer preferred booking date/time on quotes - pure, shared
// validation for INV-34-PREF-WINDOW (preferred date must be at least 14
// days after the current date, in the shop's own timezone, with no
// upper bound) plus the workspace operating-hours shape used to build the
// client's 30-minute time picker. Timezone conversion itself happens
// server-side (Intl APIs, no new deps) - everything here works on plain
// ISO date strings ('YYYY-MM-DD') and minute-of-day integers so it stays
// pure and property-testable without a Date/timezone dependency.
import { z } from 'zod';
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
// Minutes from midnight, 0..1439 for open, 1..1440 for close (open < close
// enforced by the day itself only being meaningful when both are set).
const dayHoursSchema = z
    .object({
    open: z.number().int().min(0).max(1439),
    close: z.number().int().min(1).max(1440),
})
    .nullable();
export const operatingHoursSchema = z.object({
    mon: dayHoursSchema,
    tue: dayHoursSchema,
    wed: dayHoursSchema,
    thu: dayHoursSchema,
    fri: dayHoursSchema,
    sat: dayHoursSchema,
    sun: dayHoursSchema,
});
// Mirrors migration 0041's column default - Mon-Sat 08:00-18:00, Sun closed.
export const DEFAULT_OPERATING_HOURS = {
    mon: { open: 480, close: 1080 },
    tue: { open: 480, close: 1080 },
    wed: { open: 480, close: 1080 },
    thu: { open: 480, close: 1080 },
    fri: { open: 480, close: 1080 },
    sat: { open: 480, close: 1080 },
    sun: null,
};
export const PREFERRED_BOOKING_MIN_LEAD_DAYS = 14;
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be an ISO date (YYYY-MM-DD)');
// Minutes from midnight, 30-minute increments only, 0 (00:00) through 1410 (23:30).
export const preferredTimeSchema = z
    .number()
    .int()
    .min(0)
    .max(1410)
    .refine((m) => m % 30 === 0, 'must be a 30-minute increment');
export const preferredDateSchema = isoDateSchema;
// Canonical cross-field shape: date without time is fine (customer picked a
// day, not yet a slot); time without date is not (there's nothing to attach
// the time to). Both absent/null is the "no preference set" state.
export const preferredBookingSchema = z
    .object({
    preferredDate: preferredDateSchema.nullable().optional(),
    preferredTime: preferredTimeSchema.nullable().optional(),
})
    .check((ctx) => {
    if (ctx.value.preferredTime != null && ctx.value.preferredDate == null) {
        ctx.issues.push({
            code: 'custom',
            message: 'preferredTime requires preferredDate',
            input: ctx.value,
            path: ['preferredTime'],
        });
    }
});
/** Pure day-count comparison between two ISO date strings - both already
 * resolved to the shop's timezone by the caller, so this never touches
 * Date/timezone math itself (INV-34-PREF-WINDOW). The preferred date must be
 * at least `minLeadDays` days after the anchor date, with no upper bound. */
export function isPreferredDateInWindow(anchorDateIsoDate, preferredDateIsoDate, minLeadDays = PREFERRED_BOOKING_MIN_LEAD_DAYS) {
    const anchorDate = Date.parse(`${anchorDateIsoDate}T00:00:00Z`);
    const preferredDate = Date.parse(`${preferredDateIsoDate}T00:00:00Z`);
    const diffDays = Math.round((preferredDate - anchorDate) / 86_400_000);
    return diffDays >= minLeadDays;
}
const UTC_DAY_TO_WEEKDAY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
export function weekdayOfIsoDate(isoDate) {
    return UTC_DAY_TO_WEEKDAY[new Date(`${isoDate}T00:00:00Z`).getUTCDay()];
}
export function addDaysToIsoDate(isoDate, days) {
    const d = new Date(`${isoDate}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}
/** True when `minutes` falls inside the shop's operating hours for the
 * weekday `isoDate` lands on, and is itself a valid 30-minute increment. */
export function isTimeWithinOperatingHours(hours, isoDate, minutes) {
    if (minutes % 30 !== 0)
        return false;
    const day = hours[weekdayOfIsoDate(isoDate)];
    if (!day)
        return false;
    return minutes >= day.open && minutes < day.close;
}
//# sourceMappingURL=booking.js.map